import type { AppFeature, Features } from '../../api/featuresApi';

/** One entry from `changelog/*.md`, as written by `scripts/changelog.js`. */
export type ChangelogCategory = 'nytt' | 'forbattrat' | 'atgardat';

export interface ChangelogEntry {
    slug: string;
    category: ChangelogCategory;
    area?: string;
    feature?: AppFeature;
    role?: 'admin' | 'superadmin';
    text: string;
}

/** A released version (`version` from its tag) or the unreleased group (`version: null`). `date` is not shown. */
export interface ChangelogVersion {
    version: string | null;
    date: string | null;
    entries: ChangelogEntry[];
}

export const CATEGORY_LABELS: Record<ChangelogCategory, string> = {
    nytt: 'Nytt',
    forbattrat: 'Förbättrat',
    atgardat: 'Åtgärdat',
};

export const CATEGORY_ORDER: ChangelogCategory[] = ['nytt', 'forbattrat', 'atgardat'];

export interface ChangelogViewer {
    isAdmin: boolean;
    isSuperadmin: boolean;
    /** Instance feature toggles, null while loading (everything shows). */
    features: Features | null;
}

const isVisible = (entry: ChangelogEntry, viewer: ChangelogViewer): boolean => {
    if (entry.role === 'superadmin' && !viewer.isSuperadmin) return false;
    if (entry.role === 'admin' && !viewer.isAdmin) return false;
    if (entry.feature && viewer.features && viewer.features[entry.feature] === false) return false;
    return true;
};

/** The versions with something this viewer can see, entries grouped by category in display order. */
export function visibleVersions(
    versions: ChangelogVersion[],
    viewer: ChangelogViewer,
): Array<ChangelogVersion & { groups: Array<{ category: ChangelogCategory; entries: ChangelogEntry[] }> }> {
    return versions
        .map(version => {
            const entries = version.entries.filter(entry => isVisible(entry, viewer));
            const groups = CATEGORY_ORDER
                .map(category => ({ category, entries: entries.filter(entry => entry.category === category) }))
                .filter(group => group.entries.length > 0);
            return { ...version, entries, groups };
        })
        .filter(version => version.entries.length > 0);
}
